/**
 * Material erfassen — `PWA-004`.
 *
 * **Abrechnungsfähig, nicht als freie Notiz** (`CLAUDE.md`). Deshalb hat ein
 * Posten Bezeichnung, Menge und Einheit als getrennte Felder statt einer
 * Zeile „2x LS-Schalter B16". Der Unterschied fällt erst bei der Übergabe an
 * die Branchensoftware auf — und dann ist der Bestand voll unstrukturierter
 * Zeilen, die jemand von Hand nacharbeitet.
 *
 * **Ein Materialkatalog mit Barcode gehört nach `07` §7.15 hinter den
 * Piloten.** Das Feld ist deshalb ein Freitext mit Vorschlägen aus dem, was
 * im Betrieb schon verbaut wurde — keine Artikelnummer erzwungen, aber eine
 * angeboten, wo sie bekannt ist.
 */

import { useMemo, useState } from "react";

import type { Einsatz } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { useMonteur } from "../../src/Sitzung.js";

/** Die Einheiten, die im Elektrohandwerk tatsächlich vorkommen. */
const EINHEITEN = ["Stück", "Meter", "Packung", "Rolle", "Satz", "Liter"] as const;

export function Materialerfassung({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, auflage, zustandVon, buchMaterial } = useMonteur();

  const gesendet = bestand.material.filter(
    (m) => formatResourceRef(m.einsatz) === formatResourceRef(einsatz.ref),
  );
  const eigene = auflage(einsatz).material;
  const zustand = zustandVon(einsatz);
  const gesperrt = zustand === "geprueft" || zustand === "storniert";

  /**
   * Was der Betrieb schon einmal verbaut hat.
   *
   * Aus dem vorhandenen Bestand statt aus einer gepflegten Liste: Ein Katalog,
   * den jemand pflegen müsste, ist nach `07` §7.15 ohnehin erst nach dem
   * Piloten dran — und was zehnmal verbaut wurde, ist der bessere Vorschlag
   * als eine Liste, die niemand aktuell hält.
   */
  const bekannt = useMemo(() => {
    const namen = new Map<string, string | undefined>();
    for (const m of bestand.material) {
      if (!namen.has(m.bezeichnung)) namen.set(m.bezeichnung, m.artikelnummer);
    }
    return [...namen.entries()].sort((a, b) => a[0].localeCompare(b[0], "de"));
  }, [bestand]);

  const [offen, setzeOffen] = useState(false);
  const [bezeichnung, setzeBezeichnung] = useState("");
  const [menge, setzeMenge] = useState("1");
  const [einheit, setzeEinheit] = useState<string>("Stück");
  const [abrechenbar, setzeAbrechenbar] = useState(true);
  const [fehler, setzeFehler] = useState<string | null>(null);

  function speichern() {
    const zahl = Number(menge.replace(",", "."));
    if (!bezeichnung.trim()) {
      setzeFehler("Ohne Bezeichnung lässt sich der Posten nicht abrechnen.");
      return;
    }
    if (!Number.isFinite(zahl) || zahl <= 0) {
      setzeFehler("Die Menge muss eine Zahl größer als null sein.");
      return;
    }

    buchMaterial(einsatz, {
      bezeichnung: bezeichnung.trim(),
      menge: zahl,
      einheit,
      abrechenbar,
    });
    setzeBezeichnung("");
    setzeMenge("1");
    setzeFehler(null);
    setzeOffen(false);
  }

  return (
    <>
      <h2>Material</h2>

      {gesendet.length === 0 && eigene.length === 0 ? (
        <p className="leer leise">Noch kein Material erfasst.</p>
      ) : (
        <ul className="postenliste">
          {gesendet.map((m) => (
            <li key={m.ref.opaqueId}>
              <span>
                {m.bezeichnung}
                {m.artikelnummer && <span className="leise"> · {m.artikelnummer}</span>}
                {!m.abrechenbar && (
                  <span className="abzeichen abzeichen--neutral">nicht abrechenbar</span>
                )}
              </span>
              <span>
                {m.menge} {m.einheit}
              </span>
            </li>
          ))}
          {eigene.map((m) => (
            <li key={m.id} className="postenliste__wartend">
              <span>
                {m.bezeichnung}
                <span className="abzeichen abzeichen--warnung">wartet auf Upload</span>
                {!m.abrechenbar && (
                  <span className="abzeichen abzeichen--neutral">nicht abrechenbar</span>
                )}
              </span>
              <span>
                {m.menge} {m.einheit}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!gesperrt
        && (offen ? (
          <div className="formular">
            <label>
              Bezeichnung
              <input
                type="text"
                list="materialvorschlaege"
                value={bezeichnung}
                onChange={(e) => setzeBezeichnung(e.target.value)}
                placeholder="z. B. Leitungsschutzschalter B16, 1-polig"
                autoFocus
              />
            </label>
            <datalist id="materialvorschlaege">
              {bekannt.map(([name, nummer]) => (
                <option key={name} value={name}>
                  {nummer ?? ""}
                </option>
              ))}
            </datalist>

            <div className="formular__paar">
              <label>
                Menge
                <input
                  type="text"
                  inputMode="decimal"
                  value={menge}
                  onChange={(e) => setzeMenge(e.target.value)}
                />
              </label>
              <label>
                Einheit
                <select value={einheit} onChange={(e) => setzeEinheit(e.target.value)}>
                  {EINHEITEN.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="schalter">
              <input
                type="checkbox"
                checked={abrechenbar}
                onChange={(e) => setzeAbrechenbar(e.target.checked)}
              />
              Abrechenbar
            </label>

            {fehler && <p className="marke marke--notfall">{fehler}</p>}

            <div className="knopfleiste">
              <button
                type="button"
                className="knopfleiste__knopf knopfleiste__knopf--haupt"
                onClick={speichern}
              >
                Material buchen
              </button>
              <button
                type="button"
                className="knopfleiste__knopf"
                onClick={() => {
                  setzeOffen(false);
                  setzeFehler(null);
                }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        ) : (
          <div className="knopfleiste">
            <button
              type="button"
              className="knopfleiste__knopf"
              onClick={() => setzeOffen(true)}
            >
              Material erfassen
            </button>
          </div>
        ))}

      <p className="leise fussnote">
        Nicht verbautes Material gehört abgezogen, nicht im Fahrzeug gelassen — die
        Wissensseite „Materialrückgabe" sagt, warum.
      </p>
    </>
  );
}
